import React, { useState, useEffect } from 'react';
import {
  FileText,
  Upload,
  Download,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Award,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Star
} from 'lucide-react';
import { TechCard, TechButton, TechBadge } from './TechUI';
import { careerAPI } from '../utils/api';
import { toast } from 'react-hot-toast';
import UpgradePrompt from './UpgradePrompt';

const ResumeImprovementModule = ({ userId }) => {
  const [resumeFile, setResumeFile] = useState(null);
  const [profile, setProfile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [limitError, setLimitError] = useState(null);

  useEffect(() => {
    // Load user profile from localStorage or API
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile));
    }
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      toast.error('Please upload a PDF or DOCX file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit (matching backend)
      toast.error('File size must be less than 10MB');
      return;
    }

    setResumeFile(file);
    toast.success('Resume file selected! Click "Analyze Resume" to process it.');
  };

  const handleAnalyze = async () => {
    if (!resumeFile || !profile) {
      toast.error('Please upload a resume and ensure your profile is complete');
      return;
    }

    setLoading(true);
    try {
      // Send file directly to backend for parsing
      const formData = new FormData();
      formData.append('resume_file', resumeFile);
      formData.append('profile', JSON.stringify(profile));

      const response = await careerAPI.generateResumeImprovementReportWithFile(formData);
      setAnalysis(response);
      setLimitError(null);
      toast.success('Resume analysis complete!');
    } catch (error) {
      console.error('Error analyzing resume:', error);
      if (error.isLimitError && error.limitInfo) {
        setLimitError(error.limitInfo);
      } else {
        toast.error(error.message || 'Failed to analyze resume');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'text-red-400 bg-red-900/30 border-red-700';
      case 'medium':
        return 'text-yellow-400 bg-yellow-900/30 border-yellow-700';
      case 'low':
        return 'text-green-400 bg-green-900/30 border-green-700';
      default:
        return 'text-slate-400 bg-slate-900/30 border-slate-700';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-16 h-16 text-emerald-500 animate-spin mb-4" />
        <p className="text-xl text-white font-semibold">Analyzing your resume...</p>
        <p className="text-slate-400 mt-2">This may take 30-60 seconds</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Upload Section */}
      <TechCard className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Resume Improvement AI</h2>
            <p className="text-slate-400">Upload your resume to receive actionable insights and ATS optimization</p>
          </div>
          <FileText className="w-12 h-12 text-emerald-400" />
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <label className="flex-grow cursor-pointer">
            <div className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center hover:border-emerald-500 transition-all bg-slate-800/50">
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">
                {resumeFile ? resumeFile.name : 'Click to upload resume'}
              </p>
              <p className="text-slate-500 text-sm">PDF or DOCX (max 5MB)</p>
            </div>
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>

          <div className="flex flex-col justify-center">
            <TechButton
              onClick={handleAnalyze}
              disabled={!resumeFile || loading}
              icon={RefreshCw}
              className="text-lg px-8 py-4"
            >
              Analyze Resume
            </TechButton>
          </div>
        </div>
      </TechCard>

      {/* Limit Error - Show Upgrade Prompt */}
      {limitError && (
        <UpgradePrompt
          title="Resume Analysis Limit Reached"
          message={limitError.message || "You've reached your resume analysis limit. Upgrade to continue analyzing resumes."}
          feature="Resume Analysis"
          limit={limitError.limit}
          used={limitError.used}
        />
      )}

      {/* Analysis Results */}
      {analysis && !limitError && (
        <>
          {/* Score Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TechCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Overall Score</h3>
                <Award className={`w-8 h-8 ${getScoreColor(analysis.overall_score)}`} />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className={`text-5xl font-bold ${getScoreColor(analysis.overall_score)}`}>
                  {analysis.overall_score}
                </span>
                <span className="text-slate-400 text-2xl">/100</span>
              </div>
              <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${analysis.overall_score >= 80
                      ? 'bg-emerald-500'
                      : analysis.overall_score >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  style={{ width: `${analysis.overall_score}%` }}
                />
              </div>
            </TechCard>

            <TechCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">ATS Compatibility</h3>
                <TrendingUp className={`w-8 h-8 ${getScoreColor(analysis.ats_score)}`} />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className={`text-5xl font-bold ${getScoreColor(analysis.ats_score)}`}>
                  {analysis.ats_score}
                </span>
                <span className="text-slate-400 text-2xl">/100</span>
              </div>
              <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${analysis.ats_score >= 80
                      ? 'bg-emerald-500'
                      : analysis.ats_score >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  style={{ width: `${analysis.ats_score}%` }}
                />
              </div>
            </TechCard>

            <TechCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Action Items</h3>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-5xl font-bold text-white">
                  {analysis.improvements?.length || 0}
                </span>
                <span className="text-slate-400 text-2xl">fixes</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <TechBadge type="error">
                  {analysis.improvements?.filter(i => i.priority === 'High').length || 0} High
                </TechBadge>
                <TechBadge type="warning">
                  {analysis.improvements?.filter(i => i.priority === 'Medium').length || 0} Medium
                </TechBadge>
                <TechBadge type="success">
                  {analysis.improvements?.filter(i => i.priority === 'Low').length || 0} Low
                </TechBadge>
              </div>
            </TechCard>
          </div>

          {/* Improvement Items */}
          <TechCard className="p-8">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 text-yellow-400 mr-3" />
              Actionable Improvements
            </h2>
            <div className="space-y-4">
              {analysis.improvements?.map((improvement, index) => (
                <div
                  key={index}
                  className="border border-slate-700 rounded-xl overflow-hidden bg-slate-800/30"
                >
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-700/30 transition-all flex items-center justify-between"
                    onClick={() => toggleCategory(index)}
                  >
                    <div className="flex items-center space-x-4 flex-grow">
                      <div className={`px-3 py-1 rounded-lg border ${getPriorityColor(improvement.priority)}`}>
                        <span className="text-sm font-bold">{improvement.priority}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">{improvement.category}</h3>
                        <p className="text-slate-400 text-sm mt-1">{improvement.issue}</p>
                      </div>
                    </div>
                    {expandedCategories[index] ? (
                      <ChevronUp className="w-6 h-6 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-slate-400" />
                    )}
                  </div>

                  {expandedCategories[index] && (
                    <div className="p-5 border-t border-slate-700 bg-slate-900/50">
                      <h4 className="text-emerald-400 font-semibold mb-2 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        How to Fix:
                      </h4>
                      <p className="text-white mb-4 leading-relaxed">{improvement.fix}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TechCard>

          {/* Before vs After Resume Comparison */}
          {analysis.rewritten_sections && (
            <TechCard className="p-8">
              <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
                <Star className="w-8 h-8 text-yellow-400 mr-3" />
                Before vs After — AI Rewrite
              </h2>
              <p className="text-slate-400 text-sm mb-6">See how AI transforms your resume. Green = improved. Red = original.</p>

              {analysis.rewritten_sections.summary && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-emerald-400 mb-3">Professional Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Before */}
                    <div className="bg-red-950/30 border border-red-500/20 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-mono font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded">BEFORE</span>
                      </div>
                      <p className="text-slate-400 leading-relaxed line-through decoration-red-500/40 text-sm italic">
                        {analysis.original_summary || "Your current summary could be stronger. See the improved version →"}
                      </p>
                    </div>
                    {/* After */}
                    <div className="bg-emerald-950/30 border border-emerald-500/20 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">AFTER</span>
                        <span className="text-[10px] text-emerald-400 font-mono">✨ AI Enhanced</span>
                      </div>
                      <p className="text-white leading-relaxed text-sm">{analysis.rewritten_sections.summary}</p>
                    </div>
                  </div>
                </div>
              )}

              {analysis.rewritten_sections.experience_bullet_points && (
                <div>
                  <h3 className="text-xl font-semibold text-emerald-400 mb-3">Experience Bullet Points</h3>
                  <div className="space-y-3">
                    {analysis.rewritten_sections.experience_bullet_points.map((bullet, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-red-950/20 border border-red-500/10 rounded-lg p-3 flex items-start space-x-2">
                          <span className="text-red-500 text-xs font-mono mt-0.5 shrink-0">✗</span>
                          <span className="text-slate-400 text-sm line-through decoration-red-500/30 italic">
                            {analysis.original_bullets?.[index] || `Original bullet point #${index + 1} — weak action verbs and no metrics`}
                          </span>
                        </div>
                        <div className="bg-emerald-950/20 border border-emerald-500/10 rounded-lg p-3 flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-white text-sm leading-relaxed">{bullet}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TechCard>
          )}

          {/* Tips Section */}
          {analysis.tips && analysis.tips.length > 0 && (
            <TechCard className="p-8">
              <h2 className="text-3xl font-bold text-white mb-6">💡 Pro Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.tips.map((tip, index) => (
                  <div
                    key={index}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex items-start space-x-3"
                  >
                    <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <p className="text-white leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </TechCard>
          )}

          {/* Download Section */}
          <div className="flex justify-center">
            <TechButton
              onClick={() => toast.success('Download feature coming soon!')}
              icon={Download}
              className="text-lg px-8 py-4"
            >
              Download Improved Resume
            </TechButton>
          </div>
        </>
      )}

      {/* Empty State */}
      {!analysis && !loading && (
        <TechCard className="p-12 text-center">
          <FileText className="w-20 h-20 text-slate-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">No Analysis Yet</h3>
          <p className="text-slate-400 max-w-md mx-auto">
            Upload your resume and click "Analyze" to receive personalized improvement suggestions and ATS optimization tips.
          </p>
        </TechCard>
      )}
    </div>
  );
};

export default ResumeImprovementModule;

